import { Layout, Layouts } from "react-grid-layout";
import { WidgetWidth, WidgetHeight, WidgetInstance, DashboardWidget } from "../components/DashboardWidgetConfig";

export interface WidgetLayoutInfo {
  id: string;
  width: WidgetWidth;
  height?: WidgetHeight;
}

export type DashboardBreakpoint = "lg" | "md" | "sm";
export const LEFT_COLS: Record<DashboardBreakpoint, number> = { lg: 4, md: 2, sm: 1 };
// Right area stays at 2 cols at every breakpoint: the QuickAction shortcuts are
// w=1 widgets that need to remain half-width (so two fit side-by-side) even
// when the dashboard stacks on mobile. Collapsing to 1 col here would blow the
// shortcuts up to full viewport width.
export const RIGHT_COLS: Record<DashboardBreakpoint, number> = { lg: 2, md: 2, sm: 2 };
export const LEFT_ROW_HEIGHT = 3;
export const RIGHT_ROW_HEIGHT = 2;

const BREAKPOINTS: DashboardBreakpoint[] = ["lg", "md", "sm"];

const packForCols = (
  widgets: WidgetLayoutInfo[],
  cols: number,
  rowHeight: number
): Layout[] => {
  const result: Layout[] = [];
  let currentX = 0;
  let currentY = 0;
  let rowMaxHeight = 0;

  for (const widget of widgets) {
    const { id, width, height = 1 } = widget;
    const w = Math.min(width, cols);
    const h = rowHeight * height;

    if (currentX + w > cols) {
      currentX = 0;
      currentY += rowMaxHeight || rowHeight;
      rowMaxHeight = 0;
    }

    result.push({ i: id, x: currentX, y: currentY, w, h });

    currentX += w;
    rowMaxHeight = Math.max(rowMaxHeight, h);
  }

  return result;
};

const generateAreaLayouts = (
  widgets: WidgetLayoutInfo[] | null | undefined,
  colsPerBp: Record<DashboardBreakpoint, number>,
  rowHeight: number
): Layouts => {
  const layouts: Layouts = { lg: [], md: [], sm: [] };
  if (!Array.isArray(widgets)) return layouts;

  for (const bp of BREAKPOINTS) {
    layouts[bp] = packForCols(widgets, colsPerBp[bp], rowHeight);
  }
  return layouts;
};

export const generateLeftAreaLayouts = (
  widgets: WidgetLayoutInfo[] | null | undefined
): Layouts => generateAreaLayouts(widgets, LEFT_COLS, LEFT_ROW_HEIGHT);

export const generateRightAreaLayouts = (
  widgets: WidgetLayoutInfo[] | null | undefined
): Layouts => generateAreaLayouts(widgets, RIGHT_COLS, RIGHT_ROW_HEIGHT);

/**
 * Find the first available slot for a new widget in an existing layout.
 * Scans top-to-bottom, left-to-right. Fills gaps between existing widgets
 * before placing below all widgets.
 *
 * Pass `layoutOverride` to scan a specific breakpoint's layout; otherwise
 * the function defaults to `existingLayouts.lg`.
 */
export function findFirstAvailableSlot(
    existingLayouts: Layouts,
    newWidth: number,
    newHeight: number,
    cols: number,
    layoutOverride?: Layout[]
): { x: number; y: number } {
    const layout = layoutOverride ?? existingLayouts.lg ?? [];

    if (layout.length === 0) {
        return { x: 0, y: 0 };
    }

    let maxY = 0;
    for (const item of layout) {
        maxY = Math.max(maxY, item.y + item.h);
    }

    const totalRows = maxY + newHeight;
    const occupancy: boolean[][] = Array.from({ length: totalRows }, () =>
        Array(cols).fill(false)
    );

    for (const item of layout) {
        for (let row = item.y; row < item.y + item.h; row++) {
            for (let col = item.x; col < item.x + item.w; col++) {
                if (row < totalRows && col < cols) {
                    occupancy[row][col] = true;
                }
            }
        }
    }

    for (let y = 0; y <= totalRows - newHeight; y++) {
        for (let x = 0; x <= cols - newWidth; x++) {
            let fits = true;
            for (let dy = 0; dy < newHeight && fits; dy++) {
                for (let dx = 0; dx < newWidth && fits; dx++) {
                    if (occupancy[y + dy][x + dx]) {
                        fits = false;
                    }
                }
            }
            if (fits) {
                return { x, y };
            }
        }
    }

    return { x: 0, y: maxY };
}

/**
 * Build a layout from an ordered list of widget instances using gap-filling placement
 * for every breakpoint. Widths are clamped per-bp so a wider widget gracefully
 * shrinks to the narrower grid.
 */
export const buildLayoutFromInstances = (
  instances: WidgetInstance[],
  widgetTypes: DashboardWidget[],
  area: 'left' | 'right'
): Layouts => {
  const colsPerBp = area === 'left' ? LEFT_COLS : RIGHT_COLS;
  const rowHeight = area === 'left' ? LEFT_ROW_HEIGHT : RIGHT_ROW_HEIGHT;
  const layouts: Layouts = { lg: [], md: [], sm: [] };

  for (const bp of BREAKPOINTS) {
    const cols = colsPerBp[bp];
    for (const instance of instances) {
      const widget = widgetTypes.find(w => w.type === instance.type);
      if (!widget) continue;
      const h = rowHeight * (widget.height || 1);
      const w = Math.min(widget.width, cols);
      const { x, y } = findFirstAvailableSlot(layouts, w, h, cols, layouts[bp]);
      layouts[bp]!.push({ i: instance.instanceId, x, y, w, h });
    }
  }

  return layouts;
};

/**
 * Rebuild a layout for a narrower breakpoint from an existing `lg` layout.
 * Preserves user-authored ordering (sort by y, then x) and repacks into the
 * target col count. Used to back-fill missing breakpoint layouts on migration.
 */
export const reflowFromLg = (
  lgLayout: Layout[],
  cols: number,
  rowHeight: number
): Layout[] => {
  if (!lgLayout || lgLayout.length === 0) return [];

  const sorted = [...lgLayout].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  const widgets: WidgetLayoutInfo[] = sorted.map(item => ({
    id: item.i,
    width: item.w as WidgetWidth,
    height: Math.max(1, Math.round(item.h / rowHeight)) as WidgetHeight,
  }));

  return packForCols(widgets, cols, rowHeight);
};

/**
 * @deprecated Use generateLeftAreaLayouts or generateRightAreaLayouts instead.
 * Kept for backward compatibility during migration.
 */
export const generateLayouts = (
  widgets: string[] | null | undefined
): Layouts => {
  if (!Array.isArray(widgets)) {
    return { lg: [], md: [], sm: [] };
  }

  const widgetInfos: WidgetLayoutInfo[] = widgets.map((id) => ({
    id,
    width: 2,
  }));

  return generateLeftAreaLayouts(widgetInfos);
};
